import toast from 'react-hot-toast';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useAuth } from '../../../hooks/use-auth';
import { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { wait } from '../../../utils/wait';
import { userApi } from '../../../api/user-api';
import { useNavigate } from "react-router-dom";

export const CustomerEditForm = (props) => {
  const { user } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [redirectBool, setRedirectBool] = useState(false);
  const navigate = useNavigate();

  const redirectChange = () => setRedirectBool(true);

  const getPermissionsByRole = async (roles_id) => {
    const url = `${process.env.REACT_APP_BACK}/subdomain/${localStorage.getItem('tenant')}/api/permission-verification/get/`;
    const accessToken = localStorage.getItem("accessToken");
    try {
      const resp = await fetch(url, {
        method: "POST",
        body: JSON.stringify({ roles_id }),
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
      });
      if (!resp.ok) throw new Error(`HTTP error! Status: ${resp.status}`);
      const json = await resp.json();
      setPermissions(json.permissions);
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  useEffect(() => {
    if (user && user.roles_id) getPermissionsByRole(user.roles_id);
  }, [user]);

  const canViewAndCreate = permissions.some(
    (p) => p.component === "Apprenants" && ["Voir", "Créer"].every(
      (perm) => permissions.some(pp => pp.component === "Apprenants" && pp.permission === perm)
    )
  );

  const formik = useFormik({
    initialValues: {
      firstname: '',
      lastname: '',
      email: '',
      password: '',
      tlms_id: '',
      syna_id: '',
      submit: null,
    },
    validationSchema: Yup.object({
      firstname: Yup.string().max(255).required(),
      lastname: Yup.string().max(255).required(),
      email: Yup.string().email('Must be a valid email').max(255).required(),
      tlms_id: Yup.number().positive().integer(),
      syna_id: Yup.number().positive().integer(),
    }),
    onSubmit: async (values, helpers) => {
      try {
        const id = await userApi.createUser(user.admin_id, values);
        await wait(500);
        if (!id.id) {
          toast.error("Erreur lors de la création de l'utilisateur");
          return;
        }
        helpers.setStatus({ success: true });
        helpers.setSubmitting(false);
        toast.success('Utilisateur créé');
        if (redirectBool) {
          navigate(`/${globalThis.localStorage.getItem('tenant')}/dashboard/customers/${id.id}`);
        }
      } catch (err) {
        console.error(err);
        toast.error('Une erreur est survenue!');
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: err.message });
        helpers.setSubmitting(false);
      }
    },
  });

  const initials = formik.values.firstname ? formik.values.firstname.charAt(0).toUpperCase() : null;

  return (
    <form onSubmit={formik.handleSubmit}>
      <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>

        {/* Header */}
        <Box sx={{ height: 6, background: 'linear-gradient(90deg, #6060B8 0%, #9090D0 100%)' }} />
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 44, height: 44, borderRadius: '50%',
              backgroundColor: 'rgba(96,96,184,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}
          >
            <PersonAddOutlinedIcon sx={{ color: '#6060B8', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Nouvel apprenant</Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Remplissez les champs ci-dessous pour créer un apprenant
            </Typography>
          </Box>
          {initials && (
            <Avatar sx={{ ml: 'auto', width: 44, height: 44, backgroundColor: '#9E9E9E', color: 'white', fontWeight: 700 }}>
              {initials}
            </Avatar>
          )}
        </Box>
        <Divider />

        {/* Form fields */}
        <CardContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prénom"
                name="firstname"
                required
                autoCapitalize="characters"
                value={formik.values.firstname}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.firstname && formik.errors.firstname)}
                helperText={formik.touched.firstname && formik.errors.firstname}
                InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} /></InputAdornment> }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nom"
                name="lastname"
                required
                autoCapitalize="characters"
                value={formik.values.lastname}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.lastname && formik.errors.lastname)}
                helperText={formik.touched.lastname && formik.errors.lastname}
                InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} /></InputAdornment> }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                required
                value={formik.values.email}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.email && formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} /></InputAdornment> }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Id TLMS"
                name="tlms_id"
                value={formik.values.tlms_id}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.tlms_id && formik.errors.tlms_id)}
                helperText={formik.touched.tlms_id && formik.errors.tlms_id}
                InputProps={{ startAdornment: <InputAdornment position="start"><BadgeOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} /></InputAdornment> }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Id Syndays"
                name="syna_id"
                value={formik.values.syna_id}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                error={Boolean(formik.touched.syna_id && formik.errors.syna_id)}
                helperText={formik.touched.syna_id && formik.errors.syna_id}
                InputProps={{ startAdornment: <InputAdornment position="start"><BadgeOutlinedIcon sx={{ color: '#6060B8', fontSize: 20 }} /></InputAdornment> }}
              />
            </Grid>

          </Grid>
        </CardContent>

        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 3, py: 2, gap: 1.5, flexWrap: 'wrap' }}>
          {canViewAndCreate && (
            <Button
              type="submit"
              variant="outlined"
              disabled={formik.isSubmitting}
              startIcon={<OpenInNewOutlinedIcon />}
              onClick={redirectChange}
              sx={{ borderColor: '#6060B8', color: '#6060B8', '&:hover': { borderColor: '#4a4a9a', backgroundColor: 'rgba(96,96,184,0.06)' } }}
            >
              Créer et aller sur la fiche
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            startIcon={<SaveOutlinedIcon />}
            sx={{ backgroundColor: '#6060B8', '&:hover': { backgroundColor: '#4a4a9a' } }}
          >
            Créer
          </Button>
        </Box>

      </Card>
    </form>
  );
};
